import type { Onboarding, OnboardingStep } from '@/entities/order';
import type { TenantTrial } from '@/entities/tenant';

/**
 * Wat een bedrijf nu als eerste moet doen. Eén antwoord, zodat het blok "Aan de
 * slag" één knop heeft in plaats van drie die om aandacht vragen.
 *
 * - `choose_plan` — de gratis keuringen zijn op: zonder abonnement geen nieuwe
 *   keuring, dus dit gaat voor alles. Ook de laatste stap als de proef nog loopt
 *   en de rest klaar is.
 * - `first_inspection` — nog geen enkele keuring: daar draait alles om.
 * - `company_details`, `house_style` — de open eerste stappen, in hun volgorde.
 */
export type NextStep = 'choose_plan' | OnboardingStep;

export interface GettingStartedInput {
    trial: TenantTrial | null;
    /** De eerste stappen, of `null` als ze klaar of verborgen zijn. */
    onboarding: Onboarding | null;
    /** `false` alleen als de API zegt dat er geen keuringen zijn. */
    hasInspections: boolean;
}

/** Zijn de gratis keuringen op? Onbekend (`null`) telt niet als op. */
export function trialUsedUp(trial: TenantTrial | null): boolean {
    return trial?.inspectionsRemaining !== null && trial?.inspectionsRemaining !== undefined
        ? trial.inspectionsRemaining <= 0
        : false;
}

export function nextStepFor({
    trial,
    onboarding,
    hasInspections,
}: GettingStartedInput): NextStep | null {
    if (trialUsedUp(trial)) {
        return 'choose_plan';
    }

    if (!hasInspections) {
        return 'first_inspection';
    }

    const open = onboarding?.steps.find((step) => !step.done);

    if (open) {
        return open.key;
    }

    return trial ? 'choose_plan' : null;
}
